import { Link } from "react-router";
import { motion } from "motion/react";
import { useMe } from "../auth.tsx";
import { useApi } from "../api.ts";
import { fmtExpiry } from "../format.ts";
import { Button, Card, Icon, cn, type IconName } from "../ui/index.tsx";
import { GameArt, type LoaderLatest } from "./_shared.tsx";
import { LoaderHero } from "./member/DownloadHero.tsx";
import { InstallSteps } from "./member/InstallSteps.tsx";
import { CountUp, EASE, Springy, Stage, pop, rise, stagger, useGameMap } from "./member/kit.tsx";

const REQUIREMENTS: { icon: IconName; label: string; value: string }[] = [
  { icon: "monitor", label: "Operating system", value: "Windows 10 or 11, 64-bit" },
  { icon: "globe", label: "Connection", value: "Internet access, the loader fetches your game files" },
  { icon: "key", label: "Account", value: "An ExoWare account with an active license" },
];

function Requirements() {
  return (
    <Card title="System requirements" className="h-full">
      <motion.ul className="space-y-3" variants={stagger(0.1, 0.1)} initial="hidden" whileInView="show" viewport={{ once: true }}>
        {REQUIREMENTS.map((r) => (
          <motion.li key={r.label} variants={rise} className="flex items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-raised text-accent"><Icon name={r.icon} className="size-4" /></span>
            <div>
              <div className="kicker">{r.label}</div>
              <div className="text-sm">{r.value}</div>
            </div>
          </motion.li>
        ))}
      </motion.ul>
    </Card>
  );
}

function YourGames() {
  const me = useMe();
  const gameMap = useGameMap();
  const active = me.entitlements.filter((e) => e.active);
  return (
    <Card title="Unlocked by the loader" className="h-full"
      description={active.length ? undefined : "Games show up here once you have an active license."}
      actions={active.length ? <span className="font-mono text-xs text-muted"><CountUp value={active.length} /> ready</span> : undefined}>
      {active.length === 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/licenses"><Springy><Button variant="primary" icon="key">Redeem a key</Button></Springy></Link>
          <Link to="/pricing"><Springy><Button icon="card">See pricing</Button></Springy></Link>
        </div>
      ) : (
        <motion.ul className="flex flex-wrap gap-2" variants={stagger(0.07, 0.1)} initial="hidden" whileInView="show" viewport={{ once: true }}>
          {active.map((e) => {
            const g = gameMap.get(e.game_id);
            return (
              <motion.li key={e.game_id} variants={pop} whileHover={{ y: -2, scale: 1.04 }}
                className="flex items-center gap-2 rounded-lg border border-line bg-bg/60 py-1 pl-1 pr-3 text-sm">
                <GameArt game={{ name: e.game_name, image_url: g?.image_url ?? "", category: g?.category ?? "other" }} className="size-7 rounded-md [&_span]:text-[10px]" />
                <span className="font-medium">{e.game_name}</span>
                <span className={cn("font-mono text-[10px] uppercase tracking-wider", e.expires_at === null ? "text-accent" : "text-faint")}>{e.expires_at === null ? "Lifetime" : fmtExpiry(e.expires_at)}</span>
              </motion.li>
            );
          })}
        </motion.ul>
      )}
    </Card>
  );
}

function HelpLink({ to, icon, title, body }: { to: string; icon: IconName; title: string; body: string }) {
  return (
    <Link to={to} className="pane group flex items-center gap-4 p-4 transition-colors hover:border-accent/40 hover:bg-raised">
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-raised text-muted transition-all group-hover:bg-accent-soft group-hover:text-accent"><Icon name={icon} className="size-5" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted">{body}</span>
      </span>
      <Icon name="chevronRight" className="size-4 text-faint transition-all group-hover:translate-x-1 group-hover:text-accent" />
    </Link>
  );
}

export default function Downloads() {
  const loader = useApi<LoaderLatest>("/loader/latest");
  return (
    <Stage>
      <div className="space-y-8">
        <LoaderHero loader={loader.data} loading={loader.loading} error={loader.error} retry={() => void loader.reload()} />
        <InstallSteps />
        <motion.div className="grid gap-6 lg:grid-cols-2" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "0px 0px -10% 0px" }} transition={{ duration: 0.55, ease: EASE }}>
          <Requirements />
          <YourGames />
        </motion.div>
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, ease: EASE }}>
          <h2 className="mb-3 text-sm font-semibold">Something not working?</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <HelpLink to="/guides" icon="book" title="Troubleshooting guides" body="Setup help and fixes for common loader errors." />
            <HelpLink to="/support" icon="lifebuoy" title="Contact support" body="Open a ticket and include any error code you see." />
          </div>
        </motion.section>
      </div>
    </Stage>
  );
}
